import { useState } from "react";
import {
    Box,
    Button,
    Card,
    Chip,
    Divider,
    Stack,
    Typography,
} from "@mui/material";

const ACTION_META = {
    CREATE: { label: "Criação", color: "success" },
    UPDATE: { label: "Atualização", color: "warning" },
    DELETE: { label: "Exclusão", color: "error" },
    READ: { label: "Consulta", color: "info" },
    SUPPORT_START: { label: "Acesso de suporte iniciado", color: "secondary" },
    SUPPORT_END: { label: "Acesso de suporte encerrado", color: "secondary" },
};

function getActionMeta(action) {
    return ACTION_META[action] || { label: action || "—", color: "default" };
}

function formatDateTime(value) {
    if (!value) return "—";
    return new Date(value).toLocaleString("pt-BR");
}

function JsonBlock({ title, value }) {
    if (value === null || value === undefined) return null;

    return (
        <Box sx={{ mb: 1 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">
                {title}
            </Typography>
            <Box
                component="pre"
                sx={{
                    m: 0,
                    mt: 0.5,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: "#f5f6f8",
                    border: "1px solid #e3e6ea",
                    fontSize: 12,
                    overflowX: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }}
            >
                {JSON.stringify(value, null, 2)}
            </Box>
        </Box>
    );
}

function LogEntry({ item }) {
    const [expanded, setExpanded] = useState(false);
    const actionMeta = getActionMeta(item.action);
    const hasDetails = item.details || item.old_value || item.new_value;

    return (
        <Card variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                flexWrap="wrap"
                sx={{ rowGap: 1 }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1, minWidth: 0 }}>
                    <Chip label={actionMeta.label} color={actionMeta.color} size="small" sx={{ fontWeight: 600 }} />
                    <Typography variant="subtitle2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
                        {item.description || "—"}
                    </Typography>
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    {formatDateTime(item.created_at)}
                </Typography>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ rowGap: 1 }}>
                <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="body2" color="text.secondary">Administrador:</Typography>
                    <Typography variant="body2" fontWeight={600}>{item.admin_name || "—"}</Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="body2" color="text.secondary">Empresa:</Typography>
                    {item.company_name ? (
                        <Typography variant="body2" fontWeight={600}>{item.company_name}</Typography>
                    ) : (
                        <Chip label="Ação global" size="small" variant="outlined" />
                    )}
                </Stack>
            </Stack>

            {hasDetails && (
                <>
                    <Button
                        size="small"
                        onClick={() => setExpanded((prev) => !prev)}
                        sx={{ mt: 1, textTransform: "none", color: "var(--color-primary-background)", p: 0, minWidth: 0 }}
                    >
                        {expanded ? "Ocultar detalhes" : "Ver detalhes"}
                    </Button>

                    {expanded && (
                        <Box sx={{ mt: 1.5 }}>
                            <JsonBlock title="Detalhes" value={item.details} />
                            <JsonBlock title="Valor anterior" value={item.old_value} />
                            <JsonBlock title="Valor novo" value={item.new_value} />
                        </Box>
                    )}
                </>
            )}
        </Card>
    );
}

export default function LogsList({ logs }) {
    return (
        <Stack spacing={2}>
            {logs.map((item) => (
                <LogEntry key={item.id} item={item} />
            ))}
        </Stack>
    );
}
